import { useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { GroupType } from 'graphql-query-contracts';
import { updateProductCategoryQuery } from 'graphql-queries';

import {
  mapGqlProductCategoryToProductCategory,
  ProductCategory as DomainProductCategory,
} from 'domains-billing-web-mappers';

export class UpdateCategoryError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'UpdateCategoryError';
  }
}

export type UpdateCategoryInput = {
  productCategoryId: string;
  name: string;
  description: string;
  channelId: string;
};

export const useUpdateCategory = () => {
  const [updateCategoryMutation, { loading, error }] = useMutation(
    updateProductCategoryQuery
  );

  const updateCategory = async ({
    productCategoryId,
    name,
    description,
    channelId,
  }: UpdateCategoryInput): Promise<DomainProductCategory | undefined> => {
    const result = await updateCategoryMutation({
      variables: {
        updateProductCategoryRequest: {
          productCategoryId,
          name,
          description,
          groupId: channelId,
          groupType: GroupType.GroupTypeActivateChannel,
        },
      },
    });

    return result.data?.updateProductCategory?.productCategory
      ? mapGqlProductCategoryToProductCategory(
          result.data.updateProductCategory.productCategory
        )
      : undefined;
  };

  const updateCategoryError = useMemo(() => {
    if (!error) return undefined;

    const message =
      error instanceof Error && error.message
        ? error.message
        : 'failed to update category';

    return new UpdateCategoryError(message, { cause: error });
  }, [error]);

  return {
    updateCategory,
    loading,
    error: updateCategoryError,
  };
};
